import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from '@/hooks/useLocale';
import { ENTITY_CONFIG } from '@/core/entityConfig';
import { usePortalTasksQuery } from '../hooks/usePortalTasks';
import { socialMediaApi, SocialMediaStatus } from '../services/socialMediaApi';
import { clientIdeasService } from '../services/clientIdeasService';
import { clientService } from '../services/clientService';
import { UploadMediaModal } from '@/domains/LibraryView/components/UploadMediaModal';
import {
  SpinnerIcon,
  ImageIcon,
  VideoIcon,
  CollectionIcon,
  UploadIcon,
  GridIcon,
  ChartBarIcon,
  ChevronRightIcon,
  ClientIcon,
  DocumentIcon,
  FacebookIcon,
  InstagramIcon,
  TikTokIcon,
  CheckIcon,
  LightbulbIcon,
  WalletIcon,
} from '@/components/icons';
import type { Post } from '@/core/types/postTypes';
import { NotificationSettingsCard } from '../push/NotificationSettingsCard';
import { ClientBriefsSection } from '../components/ClientBriefsSection';
import { usePortalPlan } from '../hooks/usePortalPlan';

interface ApprovalOutletContext {
  clientId: string;
  token?: string;
  pendingMedia: any[];
  pendingPosts: Post[];
}

function SectionCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl">
      <div className="p-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
      </div>
      <div className="p-4 pt-0 space-y-3">
        {children}
      </div>
    </div>
  );
}

function ActionButton({
  onClick,
  icon,
  title,
  subtitle,
  badge,
  buttonClassName,
  subtitleClassName = 'text-gray-600',
  chevronClassName = 'text-gray-400 group-hover:text-gray-600',
}: {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  badge?: number;
  buttonClassName: string;
  subtitleClassName?: string;
  chevronClassName?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors group ${buttonClassName}`}
    >
      <div className="flex items-center gap-3">
        {icon}
        <div className="text-left">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900">{title}</p>
            {badge !== undefined && badge > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                {badge}
              </span>
            )}
          </div>
          <p className={`text-xs ${subtitleClassName}`}>{subtitle}</p>
        </div>
      </div>
      <ChevronRightIcon className={`w-5 h-5 transition-colors ${chevronClassName}`} />
    </button>
  );
}

export const ApprovalDashboardRoute: React.FC = () => {
  const { clientId, token, pendingMedia, pendingPosts } = useOutletContext<ApprovalOutletContext>();
  const navigate = useNavigate();
  const { t } = useLocale();
  const plan = usePortalPlan(clientId);

  const [status, setStatus] = useState<SocialMediaStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // "Needs client action" = PENDING and unrated. A rated PENDING idea is
  // done from the client's side (waiting on admin), so it doesn't drive
  // the badge — even though it still appears in the review list for the
  // client to revisit.
  const [pendingIdeasCount, setPendingIdeasCount] = useState(0);

  useEffect(() => {
    fetchStatus();
  }, [clientId]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    clientIdeasService.getPendingIdeas(token)
      .then((ideas) => {
        if (cancelled) return;
        // Unified predicate: PENDING + no review stamp.
        const unreviewed = ideas.filter((i) => !i.clientReviewedAt).length;
        setPendingIdeasCount(unreviewed);
      })
      .catch((err) => {
        // Quiet failure — keeps the tile in its zero state. The route
        // itself surfaces a real error when the user navigates in.
        console.warn('failed to fetch pending ideas count', err);
      });
    return () => { cancelled = true; };
  }, [token]);

  const fetchStatus = async () => {
    try {
      setIsLoading(true);
      const data = await socialMediaApi.getStatus(clientId);
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch social media status:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const facebookConnected = status?.facebook.connected || false;
  const instagramConnected = status?.instagram.connected || false;
  const tiktokConnected = status?.tiktok?.connected || false;
  const facebookPage = status?.facebook.pages[0];
  const instagramAccount = status?.instagram.accounts[0];
  const tiktokAccount = status?.tiktok?.accounts?.[0];

  // Unified "needs client action" predicate: lifecycle is pending AND
  // the client hasn't left a review signal. Same shape across ideas,
  // posts, photos, reels.
  const needsReview = (item: { clientReviewedAt?: string | null }) =>
    !item.clientReviewedAt;

  // Accurate unreviewed-pending counts come from the server (total_count of a
  // stage=pending_approval + rated=false query), NOT from the paginated
  // `pendingMedia` list — that list is a 50-item page-1 slice of ALL media, so
  // unreviewed pending items outside the newest 50 would be missed and the
  // indicator would wrongly go inactive. `rated=false` matches the reviewed
  // signal: a rating is what stamps client_reviewed_at (comments alone don't).
  const { data: reviewCounts } = useQuery({
    queryKey: ['portal-pending-review-counts', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const [photos, reels] = await Promise.all([
        clientService.getPendingMedia(clientId, 'pending_approval', 'client', 'fotos', 1, 1, false),
        clientService.getPendingMedia(clientId, 'pending_approval', 'client', 'reels', 1, 1, false),
      ]);
      return { photos: photos.totalCount, reels: reels.totalCount };
    },
  });

  // Open client-visible tasks (DD-053): the Tasks page's list, read for its count.
  const { data: openTasks } = usePortalTasksQuery(clientId, 'open');
  const openTasksCount = openTasks?.count ?? 0;
  const TaskEntityIcon = ENTITY_CONFIG.task.icon;

  // Fallback to the (capped) list count only until the accurate query resolves.
  const pendingPhotosCount = reviewCounts
    ? reviewCounts.photos
    : pendingMedia.filter(item =>
        item.mediaType === 'photo' && item.stage === 'pending_approval' && needsReview(item)
      ).length;

  const pendingReelsCount = reviewCounts
    ? reviewCounts.reels
    : pendingMedia.filter(item =>
        item.contentType === 'reels' && item.stage === 'pending_approval' && needsReview(item)
      ).length;

  const pendingPostsCount = pendingPosts.filter(needsReview).length;

  if (isLoading) {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4">
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <SpinnerIcon className="w-8 h-8 text-blue-600" />
            <p className="text-gray-600">{t('dashboard.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  const reviewButtonClass = (hasPending: boolean) =>
    hasPending
      ? 'bg-amber-50 hover:bg-amber-100'
      : 'bg-gray-50 opacity-60';

  const reviewIconContainerClass = (hasPending: boolean) =>
    `w-10 h-10 rounded-lg flex items-center justify-center ${
      hasPending ? 'bg-amber-100' : 'bg-gray-100'
    }`;

  const reviewIconClass = (hasPending: boolean) =>
    `w-5 h-5 ${hasPending ? 'text-amber-600' : 'text-gray-400'}`;

  const reviewSubtitleClass = (hasPending: boolean) =>
    hasPending ? 'text-amber-700' : 'text-gray-600';

  return (
    <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4 space-y-6">
      {/* In-production briefs — first thing on the client's home. Renders
          nothing when the client has no briefs in production. */}
      <ClientBriefsSection clientId={clientId} />

      {/* Dettagli: the client's own profile pages (DD-025). */}
      <SectionCard title={t('dashboard.details.title')} subtitle={t('dashboard.details.subtitle')}>
        <ActionButton
          onClick={() => navigate('bio')}
          icon={
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
              <ClientIcon className="w-5 h-5 text-gray-600" />
            </div>
          }
          title={t('dashboard.details.bio')}
          subtitle={t('dashboard.details.bioSub')}
          buttonClassName="bg-gray-50 hover:bg-gray-100"
        />
      </SectionCard>

      {/* Help Banner - only when no social accounts connected */}
      {!facebookConnected && !instagramConnected && !tiktokConnected && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-sm text-blue-800 font-medium mb-1">
                {t('dashboard.connectBanner.title')}
              </p>
              <p className="text-sm text-blue-700">
                {t('dashboard.connectBanner.body')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Da Revisionare */}
      <SectionCard title={t('dashboard.review.title')} subtitle={t('dashboard.review.subtitle')}>
        {/* Ideas come first — they're the upstream artefact. The count is
            still stubbed (iteration 1 of the client-idea-review work);
            iteration 2 wires it into the outlet context like the others. */}
        <ActionButton
          onClick={() => navigate('ideas')}
          icon={
            <div className={reviewIconContainerClass(pendingIdeasCount > 0)}>
              <LightbulbIcon className={reviewIconClass(pendingIdeasCount > 0)} />
            </div>
          }
          title={t('dashboard.review.ideas')}
          subtitle={pendingIdeasCount > 0
            ? `${pendingIdeasCount} ${t('dashboard.review.ideasCount')}`
            : t('dashboard.review.ideasNone')}
          badge={pendingIdeasCount}
          buttonClassName={reviewButtonClass(pendingIdeasCount > 0)}
          subtitleClassName={reviewSubtitleClass(pendingIdeasCount > 0)}
        />

        <ActionButton
          onClick={() => navigate('tasks')}
          icon={
            <div className={reviewIconContainerClass(openTasksCount > 0)}>
              <TaskEntityIcon className={reviewIconClass(openTasksCount > 0)} />
            </div>
          }
          title={t('dashboard.review.tasks')}
          subtitle={openTasksCount > 0
            ? `${openTasksCount} ${t('dashboard.review.tasksCount')}`
            : t('dashboard.review.tasksNone')}
          badge={openTasksCount}
          buttonClassName={reviewButtonClass(openTasksCount > 0)}
          subtitleClassName={reviewSubtitleClass(openTasksCount > 0)}
        />

        <ActionButton
          onClick={() => navigate('review/media?stage=pending_approval')}
          icon={
            <div className={reviewIconContainerClass(pendingPhotosCount > 0)}>
              <ImageIcon className={reviewIconClass(pendingPhotosCount > 0)} />
            </div>
          }
          title={t('dashboard.review.photos')}
          subtitle={pendingPhotosCount > 0
            ? `${pendingPhotosCount} ${t('dashboard.review.photosCount')}`
            : t('dashboard.review.photosNone')}
          badge={pendingPhotosCount}
          buttonClassName={reviewButtonClass(pendingPhotosCount > 0)}
          subtitleClassName={reviewSubtitleClass(pendingPhotosCount > 0)}
        />

        <ActionButton
          onClick={() => navigate('reel?stage=pending_approval')}
          icon={
            <div className={reviewIconContainerClass(pendingReelsCount > 0)}>
              <VideoIcon className={reviewIconClass(pendingReelsCount > 0)} />
            </div>
          }
          title={t('dashboard.review.reels')}
          subtitle={pendingReelsCount > 0
            ? `${pendingReelsCount} ${t('dashboard.review.reelsCount')}`
            : t('dashboard.review.reelsNone')}
          badge={pendingReelsCount}
          buttonClassName={reviewButtonClass(pendingReelsCount > 0)}
          subtitleClassName={reviewSubtitleClass(pendingReelsCount > 0)}
        />

        <ActionButton
          onClick={() => navigate('posts')}
          icon={
            <div className={reviewIconContainerClass(pendingPostsCount > 0)}>
              <DocumentIcon className={reviewIconClass(pendingPostsCount > 0)} />
            </div>
          }
          title={t('dashboard.review.posts')}
          subtitle={pendingPostsCount > 0
            ? `${pendingPostsCount} ${t('dashboard.review.postsCount')}`
            : t('dashboard.review.postsNone')}
          badge={pendingPostsCount}
          buttonClassName={reviewButtonClass(pendingPostsCount > 0)}
          subtitleClassName={reviewSubtitleClass(pendingPostsCount > 0)}
        />

        <ActionButton
          onClick={() => navigate('planning')}
          icon={
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center group-hover:bg-orange-200 transition-colors">
              <GridIcon className="w-5 h-5 text-orange-600" />
            </div>
          }
          title={t('dashboard.planning.grid')}
          subtitle={t('dashboard.planning.gridSub')}
          buttonClassName="bg-linear-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100"
          chevronClassName="text-orange-400 group-hover:text-orange-600"
        />
      </SectionCard>

      {/* Libreria e Contenuti */}
      <SectionCard title={t('dashboard.library.title')} subtitle={t('dashboard.library.subtitle')}>
        <ActionButton
          onClick={() => navigate('review/media')}
          icon={
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
              <CollectionIcon className="w-5 h-5 text-green-600" />
            </div>
          }
          title={t('dashboard.library.allPhotos')}
          subtitle={t('dashboard.library.viewAll')}
          buttonClassName="bg-gray-50 hover:bg-gray-100"
        />

        <ActionButton
          onClick={() => navigate('reel')}
          icon={
            <div className="w-10 h-10 rounded-lg bg-fuchsia-100 flex items-center justify-center group-hover:bg-fuchsia-200 transition-colors">
              <CollectionIcon className="w-5 h-5 text-fuchsia-600" />
            </div>
          }
          title={t('dashboard.library.allReels')}
          subtitle={t('dashboard.library.viewAll')}
          buttonClassName="bg-gray-50 hover:bg-gray-100"
        />

        <ActionButton
          onClick={() => setShowUploadModal(true)}
          icon={
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <UploadIcon className="w-5 h-5 text-blue-600" />
            </div>
          }
          title={t('dashboard.library.upload')}
          subtitle={t('dashboard.library.uploadSub')}
          buttonClassName="bg-linear-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100"
          chevronClassName="text-blue-400 group-hover:text-blue-600"
        />
      </SectionCard>

      {/* Report */}
      <SectionCard title={t('dashboard.planning.title')} subtitle={t('dashboard.planning.subtitle')}>
        <ActionButton
          onClick={() => navigate('report')}
          icon={
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
              <ChartBarIcon className="w-5 h-5 text-indigo-600" />
            </div>
          }
          title={t('dashboard.planning.report')}
          subtitle={t('dashboard.planning.reportSub')}
          buttonClassName="bg-linear-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100"
          chevronClassName="text-indigo-400 group-hover:text-indigo-600"
        />
      </SectionCard>

      {/* The client's own plan: shown only when the server lets this session see it (its own contacts) */}
      {plan.data && (
        <SectionCard title={t('dashboard.plan.title')} subtitle={t('dashboard.plan.subtitle')}>
          <ActionButton
            onClick={() => navigate(`/client/${clientId}/piano`)}
            icon={
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                <WalletIcon className="w-5 h-5 text-emerald-600" />
              </div>
            }
            title={t('dashboard.plan.open')}
            subtitle={t('dashboard.plan.openSub')}
            buttonClassName="bg-linear-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100"
            chevronClassName="text-emerald-400 group-hover:text-emerald-600"
          />
        </SectionCard>
      )}

      {/* I Tuoi Account Social */}
      <SectionCard title={t('dashboard.social.title')} subtitle={t('dashboard.social.subtitle')}>
        {/* Facebook Status */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              facebookConnected ? 'bg-blue-100' : 'bg-gray-200'
            }`}>
              <FacebookIcon className={`w-5 h-5 ${facebookConnected ? 'text-blue-600' : 'text-gray-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Facebook</p>
              {facebookConnected && facebookPage ? (
                <p className="text-xs text-gray-600 truncate">{facebookPage.pageName}</p>
              ) : (
                <p className="text-xs text-gray-500">{t('dashboard.social.notConnected')}</p>
              )}
            </div>
          </div>
          {facebookConnected ? (
            <div className="flex items-center gap-1 text-green-600">
              <CheckIcon className="w-4 h-4" />
              <span className="text-xs font-medium">{t('dashboard.social.connected')}</span>
            </div>
          ) : (
            <span className="text-xs text-gray-500">{t('dashboard.social.inactive')}</span>
          )}
        </div>

        {/* Instagram Status */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              instagramConnected ? 'bg-pink-100' : 'bg-gray-200'
            }`}>
              <InstagramIcon className={`w-5 h-5 ${instagramConnected ? 'text-pink-600' : 'text-gray-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Instagram</p>
              {instagramConnected && instagramAccount ? (
                <p className="text-xs text-gray-600 truncate">@{instagramAccount.username}</p>
              ) : (
                <p className="text-xs text-gray-500">{t('dashboard.social.notConnected')}</p>
              )}
            </div>
          </div>
          {instagramConnected ? (
            <div className="flex items-center gap-1 text-green-600">
              <CheckIcon className="w-4 h-4" />
              <span className="text-xs font-medium">{t('dashboard.social.connected')}</span>
            </div>
          ) : (
            <span className="text-xs text-gray-500">{t('dashboard.social.inactive')}</span>
          )}
        </div>

        {/* TikTok Status */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              tiktokConnected ? 'bg-gray-900' : 'bg-gray-200'
            }`}>
              <TikTokIcon className={`w-5 h-5 ${tiktokConnected ? 'text-white' : 'text-gray-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">TikTok</p>
              {tiktokConnected && tiktokAccount ? (
                <p className="text-xs text-gray-600 truncate">
                  {tiktokAccount.displayName || tiktokAccount.username || tiktokAccount.openId}
                </p>
              ) : (
                <p className="text-xs text-gray-500">{t('dashboard.social.notConnected')}</p>
              )}
            </div>
          </div>
          {tiktokConnected ? (
            <div className="flex items-center gap-1 text-green-600">
              <CheckIcon className="w-4 h-4" />
              <span className="text-xs font-medium">{t('dashboard.social.connected')}</span>
            </div>
          ) : (
            <span className="text-xs text-gray-500">{t('dashboard.social.inactive')}</span>
          )}
        </div>

        {/* Manage Connection Button */}
        <button
          onClick={() => navigate('social-media')}
          className="w-full mt-2 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
        >
          {facebookConnected || instagramConnected || tiktokConnected ? t('dashboard.social.manage') : t('dashboard.social.connect')}
        </button>
      </SectionCard>

      {/* Notifiche */}
      <SectionCard title={t('dashboard.notifications.title')} subtitle={t('dashboard.notifications.subtitle')}>
        <NotificationSettingsCard clientId={clientId} />
      </SectionCard>

      {/* Upload Modal */}
      <UploadMediaModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        clientId={clientId}
        contentType="auto"
        onUploadComplete={() => {
          setShowUploadModal(false);
        }}
      />
    </div>
  );
};
